import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { TemplateConstants } from "../../constants";
import { ProjectPhase } from "../../constants/project-phase";
import { TemplatePanelData } from "../../models";
import { GetWorkflowTemplateResponse } from "../../service/query";
import { BaseResponseWithSingle, ServiceError, Status } from "../../service/Shared";
import {
  createWorkflowTemplate,
  CreateWorkflowTemplateResponse,
  updateWorkflowTemplate,
  UpdateWorkflowTemplateResponse,
} from "../../service/workflow";
import { useAuth } from "../../useAuth";
import { arrayMove, useIsLoadingWrapper } from "../../utils";
import { getWorkflowTemplateRoute } from "../../utils/routes";
import { Toast } from "../../widget";
import { DealWorkflowStage } from "./models";
import {
  createDealWorkflowStage,
  createDealWorkflowStageKeyDate,
  mapDealStagesToPhases,
  mapWorkflowTemplatePhasesToDealStages,
} from "./utils";

const MAX_DEAL_STAGE_COUNT = 10;
const DEFAULT_PROJECT_PHASES = [
  {
    keyDates: [],
    name: ProjectPhase.feasibility,
    sequence: 1,
  },
  {
    keyDates: [],
    name: ProjectPhase.design,
    sequence: 2,
  },
  {
    keyDates: [],
    name: ProjectPhase.implementation,
    sequence: 3,
  },
  {
    keyDates: [],
    name: ProjectPhase.ongoingMonitoring,
    sequence: 4,
  },
];

interface UseWorkflowTemplatePanelProps {
  workflowTemplateUuid?: string;
  workflowTemplate?: GetWorkflowTemplateResponse;
  workflowTemplateType?: string;
  show: boolean;

  onClose: () => void;
  setRefresh: Dispatch<SetStateAction<boolean>>;
}

interface UseWorkflowTemplatePanelReturnData {
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  isUpdate: boolean;
  currentTemplate: TemplatePanelData;
  dealStages: DealWorkflowStage[];
  canAddDealStage: boolean;
  canRemoveDealStage: boolean;
  canReorderDealStages: boolean;
  isDealStageReorderMode: boolean;
  setCurrentTemplate: Dispatch<SetStateAction<TemplatePanelData>>;
  handleAddDealStage: () => void;
  handleAddDealStageKeyDate: (stageId: string) => void;
  handleClosePanel: () => void;
  handleCancelDealStageReorder: () => void;
  handleEnterDealStageReorderMode: () => void;
  handleRemoveDealStage: (stageId: string) => void;
  handleRemoveDealStageKeyDate: (stageId: string, keyDateId: string) => void;
  handleReorderDealStageDrop: (droppedOnStageId: string, draggedStageId: string) => void;
  handleSaveDealStageReorder: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateDealStageDescription: (stageId: string, value: string) => void;
  handleUpdateDealStageKeyDate: (stageId: string, keyDateId: string, value: string) => void;
  handleUpdateDealStageName: (stageId: string, value: string) => void;
}

export const useWorkflowTemplatePanel = ({
  workflowTemplateUuid,
  workflowTemplate,
  workflowTemplateType,
  show,
  onClose,
  setRefresh,
}: UseWorkflowTemplatePanelProps): UseWorkflowTemplatePanelReturnData => {
  const { currentUserType } = useAuth();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ServiceError[]>();
  const [currentTemplate, setCurrentTemplate] = useState<TemplatePanelData>(TemplateConstants.DEFAULT_TEMPLATE);
  const [dealStages, setDealStages] = useState<DealWorkflowStage[]>([createDealWorkflowStage()]);
  const [originalDealStageOrder, setOriginalDealStageOrder] = useState<DealWorkflowStage[]>([]);
  const [isDealStageReorderMode, setIsDealStageReorderMode] = useState(false);

  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const isUpdate = workflowTemplate !== undefined && workflowTemplateUuid !== undefined;
  const lastDealStage = dealStages[dealStages.length - 1];
  const canAddDealStage =
    workflowTemplateType === "Deal" &&
    dealStages.length < MAX_DEAL_STAGE_COUNT &&
    (lastDealStage?.name.trim().length ?? 0) > 0;
  const canRemoveDealStage = dealStages.length > 1;
  const canReorderDealStages = dealStages.length > 1;

  useEffect(() => {
    if (!show) {
      return;
    }

    if (isUpdate && workflowTemplate) {
      setCurrentTemplate({
        description: workflowTemplate.description,
        name: workflowTemplate.name,
        projectTypeUuid: workflowTemplate.projectType?.uuid ?? null,
        validTo: workflowTemplate.validTo,
      });
      setDealStages(
        workflowTemplateType === "Deal" ? mapWorkflowTemplatePhasesToDealStages(workflowTemplate.phases) : []
      );
      setOriginalDealStageOrder([]);
      setIsDealStageReorderMode(false);
      return;
    }

    setCurrentTemplate(TemplateConstants.DEFAULT_TEMPLATE);
    setDealStages([createDealWorkflowStage()]);
    setOriginalDealStageOrder([]);
    setIsDealStageReorderMode(false);
  }, [isUpdate, show, workflowTemplate, workflowTemplateType]);

  const resetTemplatePanel = (): void => {
    setErrors([]);
    setCurrentTemplate(TemplateConstants.DEFAULT_TEMPLATE);
    setDealStages([createDealWorkflowStage()]);
    setOriginalDealStageOrder([]);
    setIsDealStageReorderMode(false);
  };

  const handleUpdateDealStageName = (stageId: string, value: string): void => {
    setDealStages((prevStages) =>
      prevStages.map((stage) => (stage.id === stageId ? { ...stage, name: value } : stage))
    );
  };

  const handleUpdateDealStageDescription = (stageId: string, value: string): void => {
    setDealStages((prevStages) =>
      prevStages.map((stage) => (stage.id === stageId ? { ...stage, description: value } : stage))
    );
  };

  const handleAddDealStage = (): void => {
    if (!canAddDealStage) {
      return;
    }

    setDealStages((prevStages) => [...prevStages, createDealWorkflowStage()]);
  };

  const handleRemoveDealStage = (stageId: string): void => {
    if (!canRemoveDealStage) {
      return;
    }

    setDealStages((prevStages) => prevStages.filter((stage) => stage.id !== stageId));
  };

  const handleAddDealStageKeyDate = (stageId: string): void => {
    setDealStages((prevStages) =>
      prevStages.map((stage) =>
        stage.id === stageId ? { ...stage, keyDates: [...stage.keyDates, createDealWorkflowStageKeyDate()] } : stage
      )
    );
  };

  const handleUpdateDealStageKeyDate = (stageId: string, keyDateId: string, value: string): void => {
    setDealStages((prevStages) =>
      prevStages.map((stage) =>
        stage.id === stageId
          ? {
              ...stage,
              keyDates: stage.keyDates.map((keyDate) =>
                keyDate.id === keyDateId ? { ...keyDate, name: value } : keyDate
              ),
            }
          : stage
      )
    );
  };

  const handleRemoveDealStageKeyDate = (stageId: string, keyDateId: string): void => {
    setDealStages((prevStages) =>
      prevStages.map((stage) => {
        if (stage.id !== stageId || stage.keyDates.slice(0, 2).some((keyDate) => keyDate.id === keyDateId)) {
          return stage;
        }

        return {
          ...stage,
          keyDates: stage.keyDates.filter((keyDate) => keyDate.id !== keyDateId),
        };
      })
    );
  };

  const handleEnterDealStageReorderMode = (): void => {
    if (!canReorderDealStages) {
      return;
    }

    setOriginalDealStageOrder(dealStages);
    setIsDealStageReorderMode(true);
  };

  const handleCancelDealStageReorder = (): void => {
    if (originalDealStageOrder.length > 0) {
      setDealStages(originalDealStageOrder);
    }
    setOriginalDealStageOrder([]);
    setIsDealStageReorderMode(false);
  };

  const handleReorderDealStageDrop = (droppedOnStageId: string, draggedStageId: string): void => {
    if (!isDealStageReorderMode || droppedOnStageId === draggedStageId) {
      return;
    }

    setDealStages((prevStages) => {
      const droppedOnStageIndex = prevStages.findIndex((stage) => stage.id === droppedOnStageId);
      const draggedStageIndex = prevStages.findIndex((stage) => stage.id === draggedStageId);

      if (droppedOnStageIndex < 0 || draggedStageIndex < 0) {
        return prevStages;
      }

      return arrayMove([...prevStages], draggedStageIndex, droppedOnStageIndex);
    });
  };

  const handleSaveDealStageReorder = (): void => {
    if (!isDealStageReorderMode) {
      return;
    }

    setOriginalDealStageOrder([]);
    setIsDealStageReorderMode(false);
  };

  const handleClosePanel = (): void => {
    resetTemplatePanel();
    onClose();
  };

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const nextErrors: ServiceError[] = [];
    if (workflowTemplateType === "Deal" && dealStages.some((stage) => stage.name.trim().length === 0)) {
      nextErrors.push({ code: "REQUIRED_STAGE_NAME", message: "Each stage must have a name" });
    }
    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }

    let res: BaseResponseWithSingle<UpdateWorkflowTemplateResponse | CreateWorkflowTemplateResponse>;

    if (isUpdate) {
      let phases;

      if (workflowTemplateType === "Deal") {
        phases = mapDealStagesToPhases(dealStages);
      } else if (workflowTemplateType === "Project") {
        phases = workflowTemplate?.phases.map((phaseItem) => ({
          keyDates: phaseItem.keyDates,
          name: phaseItem.phase,
          sequence: phaseItem.sequence,
        }));
      }

      res = await updateWorkflowTemplate({
        name: currentTemplate.name ?? workflowTemplate.name,
        description: currentTemplate.description ?? workflowTemplate.description,
        ...(phases ? { phases } : {}),
        validTo: currentTemplate.validTo ?? workflowTemplate.validTo,
        rowVersion: workflowTemplate.rowVersion,
        workflowTemplateUuid,
      });
    } else {
      let phases;

      if (workflowTemplateType === "Deal") {
        phases = mapDealStagesToPhases(dealStages);
      } else if (workflowTemplateType === "Project") {
        phases = DEFAULT_PROJECT_PHASES;
      }

      res = await createWorkflowTemplate({
        name: currentTemplate.name ?? "",
        description: currentTemplate.description ?? "",
        ...(phases && phases.length > 0 ? { phases } : {}),
        projectTypeUuid: currentTemplate.projectTypeUuid ?? "",
        type: workflowTemplateType ?? "",
        workflowTemplateType: workflowTemplateType ?? "",
        validTo: currentTemplate.validTo,
      });
    }

    if (res.status === Status.Success && res.data) {
      Toast.success({
        message: `Workflow template ${isUpdate ? "updated" : "created"} successfully`,
      });
      setRefresh((prev) => !prev);
      handleClosePanel();
      if (!isUpdate) {
        const newWorkflowTemplateUuid = (res as BaseResponseWithSingle<CreateWorkflowTemplateResponse>).data
          ?.workflowTemplateUuid;
        if (newWorkflowTemplateUuid) {
          navigate(getWorkflowTemplateRoute(newWorkflowTemplateUuid, currentUserType));
        }
      }
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  return {
    errors,

    isHandleSubmitLoading,
    isUpdate,

    currentTemplate,
    dealStages,
    canAddDealStage,
    canRemoveDealStage,
    canReorderDealStages,
    isDealStageReorderMode,
    setCurrentTemplate,

    handleAddDealStage,
    handleAddDealStageKeyDate,
    handleCancelDealStageReorder,
    handleClosePanel,
    handleEnterDealStageReorderMode,
    handleRemoveDealStage,
    handleRemoveDealStageKeyDate,
    handleReorderDealStageDrop,
    handleSaveDealStageReorder,
    handleSubmit,
    handleUpdateDealStageDescription,
    handleUpdateDealStageKeyDate,
    handleUpdateDealStageName,
  };
};
